import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DatabaseScanResult, DatabaseViolation } from '../types/database.types';
import { DATABASE_MOCK_PATTERNS, analyzeTableLevelPatterns } from '../utils/databasePatterns';
import { calculateViolationConfidence } from '../utils/confidenceScoring';
import { enhanceViolationWithFix } from '../utils/contextualAutoFix';

export const useDatabaseScanner = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState<string>('');

  // NEW: Intelligent database scanning with adaptive sampling
  const scanDatabase = async (
    rowsPerTable: number = 50,
    prioritizedTables?: string[],
    onlyPrioritized: boolean = false,
    intelligentSampling: boolean = false
  ): Promise<DatabaseScanResult> => {
    setIsScanning(true);
    const startTime = Date.now();
    const violations: DatabaseViolation[] = [];
    let tablesScanned = 0;
    let rowsAnalyzed = 0;

    try {
      // Get all tables from public schema dynamically
      setProgress('Fetching table list...');
      const { data: tables, error: tablesError } = await supabase.rpc('get_public_tables' as any);

      if (tablesError) {
        console.error('❌ Error fetching tables:', tablesError);
        throw new Error(`Could not fetch table list: ${tablesError.message}`);
      }

      let tableNames = Array.isArray(tables) ? tables : [];
      console.log(`📊 Found ${tableNames.length} tables to scan`);
      
      // Apply prioritization if specified
      if (prioritizedTables && prioritizedTables.length > 0) {
        const prioritized = tableNames.filter(t => {
          const name = typeof t === 'string' ? t : t.table_name;
          return prioritizedTables.includes(name);
        });
        
        if (onlyPrioritized) {
          tableNames = prioritized;
          console.log(`🎯 Scanning only ${tableNames.length} prioritized tables`);
        } else {
          // Scan prioritized first, then others
          const others = tableNames.filter(t => {
            const name = typeof t === 'string' ? t : t.table_name;
            return !prioritizedTables.includes(name);
          });
          tableNames = [...prioritized, ...others];
          console.log(`🎯 Prioritizing ${prioritized.length} tables, then ${others.length} others`);
        }
      }
      
      for (const tableData of tableNames) {
        const tableName = typeof tableData === 'string' ? tableData : tableData.table_name;
        if (!tableName) continue;

        try {
          setProgress(`Scanning ${tableName} (${tablesScanned + 1}/${tableNames.length})...`);
          const tableViolations = await scanTable(tableName, rowsPerTable, intelligentSampling);
          violations.push(...tableViolations);
          tablesScanned++;
          rowsAnalyzed += rowsPerTable;
        } catch (err) {
          console.warn(`Could not scan table ${tableName}:`, err);
        }
      }

      const scanDuration = Date.now() - startTime;
      setProgress('');
      setIsScanning(false);

      return {
        totalViolations: violations.length,
        violations,
        tablesScanned,
        rowsAnalyzed,
        scanDuration,
        scannedAt: new Date()
      };
    } catch (error) {
      console.error('Database scan error:', error);
      setIsScanning(false);
      setProgress('');
      throw error;
    }
  };

  const scanTable = async (
    tableName: string, 
    rowsPerTable: number = 50,
    intelligentSampling: boolean = false
  ): Promise<DatabaseViolation[]> => {
    const violations: DatabaseViolation[] = [];

    try {
      let rows: any[] = [];
      
      if (intelligentSampling && rowsPerTable >= 100) {
        // Intelligent sampling: First N, Last N, Random N from middle
        const sampleSize = Math.floor(rowsPerTable / 3);
        
        // Get first N rows
        const { data: firstRows, error: firstError }: any = await supabase
          .from(tableName as any)
          .select('*')
          .order('created_at', { ascending: true })
          .limit(sampleSize);
        
        if (firstError) throw firstError;
        
        // Get last N rows
        const { data: lastRows, error: lastError }: any = await supabase
          .from(tableName as any)
          .select('*')
          .order('created_at', { ascending: false })
          .limit(sampleSize);
        
        if (lastError) throw lastError;
        
        // Get random N rows from middle (approximate)
        const { data: randomRows, error: randomError }: any = await supabase
          .from(tableName as any)
          .select('*')
          .limit(sampleSize);
        
        if (randomError) throw randomError;
        
        rows = [...(firstRows || []), ...(lastRows || []), ...(randomRows || [])];
        console.log(`  📊 Intelligent sampling: ${rows.length} rows from ${tableName}`);
      } else {
        // Standard sampling: Just get first N rows
        const { data, error }: any = await supabase
          .from(tableName as any)
          .select('*')
          .limit(rowsPerTable);

        if (error) throw error;
        rows = data || [];
      }

      if (!rows || rows.length === 0) {
        return violations;
      }

      // Analyze table-level patterns (sequential IDs, identical timestamps)
      const tableLevelPatterns = analyzeTableLevelPatterns(rows);
      for (const pattern of tableLevelPatterns) {
        const violation: DatabaseViolation = {
          table: tableName,
          column: 'table_level',
          pattern: pattern.pattern,
          severity: pattern.severity,
          description: pattern.description,
          examples: pattern.examples,
          affectedRows: rows.length,
          confidence: 0 // Will be calculated below
        };
        
        // ✅ PHASE 3: Calculate confidence dynamically
        violation.confidence = calculateViolationConfidence(violation);
        
        // Boost confidence for clear seed data patterns
        if (pattern.description.includes('identical') && pattern.description.includes('days ago')) {
          violation.confidence = Math.max(75, violation.confidence); // Up from 60
        }
        
        // ✅ PHASE 4: Enhance with contextual auto-fix
        const enhanced = enhanceViolationWithFix(violation) as DatabaseViolation;
        
        // Lower threshold: 40% instead of 50%
        if (enhanced.confidence >= 40) {
          violations.push(enhanced);
        }
      }

      // Analyze each column for mock patterns
      const columns = Object.keys(rows[0]);
      
      for (const column of columns) {
        for (const pattern of DATABASE_MOCK_PATTERNS) {
          const matchedValues: string[] = [];
          
          for (const row of rows) {
            const value = row[column];
            if (value !== null && value !== undefined && pattern.test(value)) {
              const example = pattern.getExamples ? pattern.getExamples(value) : String(value);
              if (!matchedValues.includes(example)) {
                matchedValues.push(example);
              }
              if (matchedValues.length >= 5) break; // Limit examples
            }
          }

          if (matchedValues.length > 0) {
            const violation: DatabaseViolation = {
              table: tableName,
              column,
              pattern: pattern.name,
              severity: pattern.severity,
              description: pattern.description,
              examples: matchedValues,
              affectedRows: matchedValues.length,
              confidence: 0 // Will be calculated below
            };
            
            // ✅ PHASE 3: Calculate confidence dynamically
            violation.confidence = calculateViolationConfidence(violation);
            
            // Boost confidence for meme numbers and round prices
            if (pattern.description.includes('meme') || pattern.description.includes('test numbers')) {
              violation.confidence = Math.max(70, violation.confidence); // Up from 60
            }
            if (pattern.description.includes('round') && pattern.description.includes('prices')) {
              violation.confidence = Math.max(65, violation.confidence); // Up from 60
            }
            
            // ✅ PHASE 4: Enhance with contextual auto-fix
            const enhanced = enhanceViolationWithFix(violation) as DatabaseViolation;
            
            // Lower threshold: 40% instead of 50%
            if (enhanced.confidence >= 40) {
              violations.push(enhanced);
            }
          }
        }
      }

      return violations;
    } catch (error) {
      console.error(`Error scanning table ${tableName}:`, error);
      return violations;
    }
  };

  return {
    scanDatabase,
    isScanning,
    progress
  };
};
